/**
 * Sales-channel & touchpoint registry API client (BFF: `/api/v1/sales-channels`).
 *
 * The tenant's unified channel registry (adr-tenant-001, plan-tenant-001 Phase 5 —
 * → web-tenant-settings-service): business routes to market (own online / own retail /
 * marketplaces) and their customer touchpoints. This is where channel *identity* is managed;
 * per-channel prices and catalog bindings live in their own sections and reference these
 * canonical codes.
 */
import { apiClient } from '@/shared/api';

const BASE = '/api/v1/sales-channels';

/** Closed platform kind taxonomy (adr-tenant-001 D1). */
export type TenantSalesChannelKind = 'OWN_ONLINE' | 'OWN_RETAIL' | 'MARKETPLACE' | 'B2B_WHOLESALE';

/** Closed platform touchpoint-type taxonomy (adr-tenant-001 D1). */
export type TenantTouchpointType =
  | 'WEBSITE'
  | 'MOBILE_APP'
  | 'TELEGRAM_MINI_APP'
  | 'TELEGRAM_BOT'
  | 'MAX_MINI_APP'
  | 'MAX_BOT'
  | 'STORE'
  | 'MARKETPLACE_LISTING';

export interface TenantTouchpoint {
  code: string;
  /** `null` when the BFF saw a future taxonomy value it does not know yet. */
  type: TenantTouchpointType | null;
  name: string;
  enabled: boolean;
  externalRef?: string | null;
  channelCode?: string;
}

export interface TenantSalesChannel {
  code: string;
  /** `null` when the BFF saw a future taxonomy value it does not know yet. */
  kind: TenantSalesChannelKind | null;
  name: string;
  active: boolean;
  defaultChannel: boolean;
  sortOrder: number;
  touchpoints: TenantTouchpoint[];
}

export interface UpsertTenantSalesChannelRequest {
  code?: string;
  kind?: TenantSalesChannelKind;
  name?: string;
  active?: boolean;
  defaultChannel?: boolean;
  sortOrder?: number;
}

export interface UpsertTenantTouchpointRequest {
  code?: string;
  type?: TenantTouchpointType;
  name?: string;
  enabled?: boolean;
  externalRef?: string;
}

export const listSalesChannels = async (): Promise<TenantSalesChannel[]> =>
  (await apiClient.get<TenantSalesChannel[]>(BASE)).data;

export const createSalesChannel = async (
  body: UpsertTenantSalesChannelRequest,
): Promise<TenantSalesChannel> => (await apiClient.post<TenantSalesChannel>(BASE, body)).data;

export const updateSalesChannel = async (
  code: string,
  body: UpsertTenantSalesChannelRequest,
): Promise<TenantSalesChannel> =>
  (await apiClient.put<TenantSalesChannel>(`${BASE}/${encodeURIComponent(code)}`, body)).data;

export const deactivateSalesChannel = async (code: string): Promise<TenantSalesChannel> =>
  (await apiClient.delete<TenantSalesChannel>(`${BASE}/${encodeURIComponent(code)}`)).data;

export const updateTouchpoint = async (
  code: string,
  body: UpsertTenantTouchpointRequest,
): Promise<TenantTouchpoint> =>
  (await apiClient.put<TenantTouchpoint>(`/api/v1/touchpoints/${encodeURIComponent(code)}`, body))
    .data;
